import { useMemo, useState, useRef } from 'react';
import { gql, useQuery, useMutation } from '@apollo/client';
import { Link } from 'react-router-dom';

// React and React Native Core imports
import Page from '../../atoms/Page';
import Card from '../../atoms/Card';
import Heading from '../../atoms/Heading';

// 3rd party libraries
import dayjs from 'dayjs';
import _ from 'lodash';
import { Fzf } from 'fzf';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';

// Our Own imports
import ToggleAccessButton from '../../molecules/ToggleAccessButton';
import Avatar from '../../atoms/Avatar';
import TextInput from '../../atoms/TextInput';
import ConfirmationDialog from '../../organisms/ConfirmationDialog';
import useUser from '../../utils/hooks/useUser';

// Relative path imports
import './AllUsersPage.scss';

const ALL_USERS_GQL = gql`
  query allUsersPage_getUsers {
    user(where: { deleted_at: { _is_null: true } }, order_by: { name: asc }) {
      id
      name
      email
      phone_number
      avatar
      created_at
      deleted_at
      user_organizations(where: { deleted_at: { _is_null: true } }) {
        organization {
          id
          name
        }
      }
      user_barns(where: { deleted_at: { _is_null: true } }) {
        barn {
          id
          name
          organization {
            id
            name
          }
        }
      }
    }
  }
`;

const SOFT_DELETE_USER_GQL = gql`
  mutation softDeleteUser($user_id: uuid!, $now: bigint!) {
    update_user_by_pk(pk_columns: { id: $user_id }, _set: { deleted_at: $now }) {
      id
      deleted_at
    }
  }
`;

function AllUsersPage() {
  // --- State hooks ---
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [userToRemove, setUserToRemove] = useState(null);
  const dialogRef = useRef(null);

  // --- Atoms and other Hooks ---
  const { user: currentUser } = useUser();

  // --- Apollo/GraphQL hooks ---
  const { loading, error, data, refetch } = useQuery(ALL_USERS_GQL);
  const [softDeleteUser, { loading: removingUser, error: removeError }] = useMutation(SOFT_DELETE_USER_GQL, {
    onCompleted: () => {
      refetch();
    },
  });

  // --- Derived state ---
  const users = data?.user || [];

  // Process and filter users
  const filteredAndSortedUsers = useMemo(() => {
    if (!users) return [];

    let filtered = users.map((user) => ({
      ...user,
      organizations: _.uniqBy(user.user_organizations?.map((uo) => uo.organization) || [], 'id'),
      barns: _.uniqBy(user.user_barns?.map((ub) => ub.barn) || [], 'id'),
    }));

    // Apply search filter
    if (searchTerm) {
      const fzf = new Fzf(filtered, {
        selector: (item) => `${item.name} ${item.email} ${item.organizations.map((org) => org.name).join(' ')}`,
      });
      filtered = fzf?.find(searchTerm)?.map((e) => e.item) || [];
    }

    // Apply sorting
    if (sortField === 'name') {
      filtered = _.orderBy(filtered, ['name'], [sortDirection]);
    } else if (sortField === 'email') {
      filtered = _.orderBy(filtered, ['email'], [sortDirection]);
    } else if (sortField === 'organizations') {
      filtered = _.orderBy(filtered, [(user) => user.organizations.length], [sortDirection]);
    } else if (sortField === 'barns') {
      filtered = _.orderBy(filtered, [(user) => user.barns.length], [sortDirection]);
    } else if (sortField === 'account_created') {
      filtered = _.orderBy(filtered, ['created_at'], [sortDirection]);
    }

    return filtered;
  }, [users, searchTerm, sortField, sortDirection]);

  // --- Function definitions ---
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleRemoveUser = (user) => {
    setUserToRemove(user);
    setConfirmationDialogOpen(true);
  };

  const handleConfirmRemove = async () => {
    try {
      const now = Math.floor(Date.now() / 1000); // Convert to Unix timestamp

      const result = await softDeleteUser({
        variables: {
          user_id: userToRemove.id,
          now: now,
        },
      });

      if (removeError) {
        throw new Error(removeError.message || 'Failed to remove user');
      }

      if (!result.data?.update_user_by_pk) {
        throw new Error('User removal failed - no data returned');
      }

      toast.success(`User "${userToRemove.name}" successfully removed from system`, {
        position: 'top-right',
        autoClose: 5000,
      });
    } catch (error) {
      console.error('Error removing user:', error);
      Sentry.captureException(error);
      toast.error('Error occurred while removing user', {
        position: 'top-right',
        autoClose: 5000,
      });
    } finally {
      setConfirmationDialogOpen(false);
      setUserToRemove(null);
    }
  };

  const handleCancelRemove = () => {
    setConfirmationDialogOpen(false);
    setUserToRemove(null);
  };

  const getOrganizationsDisplay = (user) => {
    if (user.organizations.length === 0) {
      return 'No organizations';
    }
    return (
      <div className="AllUsers-OrganizationLinks">
        {user.organizations.map((org) => (
          <Link key={org.id} to={`/orgs/${org.id}`} className="AllUsers-OrganizationLink">
            {org.name}
          </Link>
        ))}
      </div>
    );
  };

  const getBarnsDisplay = (user) => {
    if (user.barns.length === 0) {
      return 'No barns';
    }
    return (
      <div className="AllUsers-BarnLinks">
        {user.barns.map((barn) => (
          <Link key={barn.id} to={`/barns/${barn.id}`} className="AllUsers-BarnLink">
            {barn.name}
          </Link>
        ))}
      </div>
    );
  };

  // Staff-only access check
  if (!currentUser?.isStaff) {
    return (
      <Page className="AllUsers">
        <div className="AllUsers-Content">
          <div className="AllUsers-Header">
            <Heading level={3}>Access Denied</Heading>
            <p>This page is only available to FeedFlo staff members.</p>
          </div>
        </div>
      </Page>
    );
  }

  if (error) return <span>Error {JSON.stringify(error)}</span>;
  if (loading) return <span>Loading...</span>;

  return (
    <Page className="AllUsers" titleSegments={['All Users']}>
      <div className="AllUsers-Content">
        <div className="AllUsers-Header">
          <Heading level={3}>All Users</Heading>
        </div>

        <div className="AllUsers-TableControls">
          <TextInput
            placeholder="Search Users"
            value={searchTerm}
            label={false}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="AllUsers-Table">
          {filteredAndSortedUsers.length > 0 ? (
            <div className="AllUsers-TableContainer">
              <div className="AllUsers-TableHeader">
                <div className="AllUsers-TableRow">
                  <div
                    className="AllUsers-TableCell AllUsers-TableHeaderCell AllUsers-TableHeaderCell--sortable"
                    onClick={() => handleSort('name')}
                  >
                    User
                    {sortField === 'name' && (
                      <span className="AllUsers-SortIndicator">{sortDirection === 'asc' ? ' ↑' : ' ↓'}</span>
                    )}
                  </div>
                  <div
                    className="AllUsers-TableCell AllUsers-TableHeaderCell AllUsers-TableHeaderCell--sortable"
                    onClick={() => handleSort('email')}
                  >
                    Email
                    {sortField === 'email' && (
                      <span className="AllUsers-SortIndicator">{sortDirection === 'asc' ? ' ↑' : ' ↓'}</span>
                    )}
                  </div>

                  <div
                    className="AllUsers-TableCell AllUsers-TableHeaderCell AllUsers-TableHeaderCell--sortable"
                    onClick={() => handleSort('organizations')}
                  >
                    Organizations
                    {sortField === 'organizations' && (
                      <span className="AllUsers-SortIndicator">{sortDirection === 'asc' ? ' ↑' : ' ↓'}</span>
                    )}
                  </div>
                  <div
                    className="AllUsers-TableCell AllUsers-TableHeaderCell AllUsers-TableHeaderCell--sortable"
                    onClick={() => handleSort('barns')}
                  >
                    Barns
                    {sortField === 'barns' && (
                      <span className="AllUsers-SortIndicator">{sortDirection === 'asc' ? ' ↑' : ' ↓'}</span>
                    )}
                  </div>
                  <div
                    className="AllUsers-TableCell AllUsers-TableHeaderCell AllUsers-TableHeaderCell--sortable"
                    onClick={() => handleSort('account_created')}
                  >
                    Account Creation Date
                    {sortField === 'account_created' && (
                      <span className="AllUsers-SortIndicator">{sortDirection === 'asc' ? ' ↑' : ' ↓'}</span>
                    )}
                  </div>
                  <div className="AllUsers-TableCell AllUsers-TableHeaderCell">Actions</div>
                </div>
              </div>
              <div className="AllUsers-TableBody">
                {filteredAndSortedUsers.map((user) => (
                  <div
                    className={`AllUsers-TableRow ${user.deleted_at ? 'AllUsers-TableRow--deleted' : ''}`}
                    key={user.id}
                  >
                    <div className="AllUsers-TableCell">
                      <Link to={`/users/${user.id}`} className="AllUsers-UserInfo">
                        <Avatar src={user.avatar} alt={`${user.name}`} size="small" />
                        <span className="AllUsers-UserName">{user.name}</span>
                      </Link>
                    </div>
                    <div className="AllUsers-TableCell">{user.email}</div>
                    <div className="AllUsers-TableCell">{getOrganizationsDisplay(user)}</div>
                    <div className="AllUsers-TableCell">{getBarnsDisplay(user)}</div>
                    <div className="AllUsers-TableCell">{dayjs(user.created_at * 1000).format('MMM DD, YYYY')}</div>
                    <div className="AllUsers-TableCell">
                      {currentUser?.isStaff && (
                        <ToggleAccessButton
                          actionType="remove"
                          onClick={() => handleRemoveUser(user)}
                          text="Remove User"
                          title="Remove user from system"
                          loading={removingUser && userToRemove?.id === user.id}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="AllUsers-NoResults">
              {searchTerm ? `No users found matching "${searchTerm}"` : 'No users found'}
            </div>
          )}
        </div>

        {users.length === 0 && (
          <div className="AllUsers-Empty">
            <Card>
              <div className="EmptyState">
                <Heading level={4}>No users found</Heading>
                <p>There are no users in the system.</p>
              </div>
            </Card>
          </div>
        )}

        <ConfirmationDialog
          dialogRef={dialogRef}
          open={confirmationDialogOpen}
          message={`Are you sure you want to remove ${userToRemove?.name} from the system?`}
          onCancel={handleCancelRemove}
          onConfirm={handleConfirmRemove}
        />
      </div>
    </Page>
  );
}

export default AllUsersPage;
